import { STORAGE } from "../storage";
import { addActionAuthenticationListener as addSocketActionListener } from "../socket/socket";
import { addSoketiActionAuthenticationListener } from "../soketi/soketi";

/**
 * Unified Action Authentication Service
 * 
 * This service provides a single interface for action authentication
 * that works with both Socket.IO and Soketi implementations.
 * 
 * It automatically detects which service is active and delegates
 * to the appropriate implementation.
 */

/**
 * Add action authentication listener
 * 
 * This function will:
 * 1. Check if Soketi is active (soketiUrl exists in storage)
 * 2. If Soketi is active, use Soketi implementation
 * 3. If Soketi is not active, use Socket.IO implementation
 * 4. If neither is available, log a warning
 * 
 * @param {Object} action - Action object with code, action, successMessage, link
 * @param {Function} callback - Optional callback to execute on success
 */
export const addActionAuthenticationListener = async (action, callback = null) => {
    try {
        // Check if Soketi is active
        const soketiUrl = await STORAGE.get("soketiUrl");
        
        if (soketiUrl) {
            // Use Soketi implementation
            addSoketiActionAuthenticationListener(action, callback);
        } else {
            // Use Socket.IO implementation
            addSocketActionListener(action, callback);
        }
    } catch (error) {
        console.error('ACTION_AUTH - Error determining service:', error);
        
        // Fallback to Socket.IO if there's an error
        try {
            addSocketActionListener(action, callback);
        } catch (fallbackError) {
            console.error('ACTION_AUTH - Fallback to Socket.IO failed:', fallbackError);
        }
    }
};

/**
 * Check if action authentication is available
 * 
 * @returns {Promise<boolean>} True if either service is available
 */
export const isActionAuthenticationAvailable = async () => {
    try {
        const soketiUrl = await STORAGE.get("soketiUrl");
        const socketUrl = await STORAGE.get("socketUrl");
        
        return !!(soketiUrl || socketUrl);
    } catch (error) {
        console.error('ACTION_AUTH - Error checking availability:', error);
        return false;
    }
};

export default {
    addActionAuthenticationListener,
    isActionAuthenticationAvailable
};
